// Operations Report — composition layer (FSJ-6-00014, Phase 1).
//
// The Operations Report is a SINGLE screen that composes several EXISTING,
// already-permissioned report endpoints. This module adapts one shared period
// {from,to} into each endpoint's own parameter contract and normalizes the
// response into a small, stable shape the view can render.
//
// Every call is wrapped so a per-section permission denial (HTTP 403) or error
// never breaks the whole page: each section resolves to
//   { status: 'ok' | 'forbidden' | 'error', data }
// so the view can show the real data, a polite "no access" state, or a retry.

import api from '@/api/client'

function inclusiveDays(from, to) {
  if (!from || !to) return 7
  const a = new Date(`${from}T12:00:00`)
  const b = new Date(`${to}T12:00:00`)
  const d = Math.round((b - a) / 86400000) + 1
  return d > 0 ? d : 1
}

// Wrap a promise-returning loader into the stable section envelope.
async function section(loader) {
  try {
    const data = await loader()
    return { status: 'ok', data }
  } catch (e) {
    const code = e?.response?.status
    if (code === 403 || code === 401) return { status: 'forbidden', data: null }
    return { status: 'error', data: null, message: e?.response?.data?.error || e?.message || 'Failed to load' }
  }
}

// 1. Channel attribution — powers KPI strip, Revenue-by-source and the funnel.
//    One call returns rows per channel (lead source) + totals, and (optionally)
//    the previous period for compare deltas.
export function loadChannelAttribution({ from, to, compare }) {
  return section(async () => {
    const params = { date_from: from, date_to: to }
    if (compare) params.compare = '1'
    const { data } = await api.get('/crm/analytics/channel-attribution/', { params })
    return data
  })
}

// 2. Cancellations summary — overall rate + by technician + by location.
export function loadCancellations({ from, to }) {
  return section(async () => {
    const { data } = await api.get('/reports/analytics/cancellations/', {
      params: { date_from: from, date_to: to },
    })
    return data
  })
}

// 3. Missed calls summary — call-center recovery health.
export function loadMissedCalls({ from, to }) {
  return section(async () => {
    const { data } = await api.get('/reports/analytics/missed-calls/', {
      params: { date_from: from, date_to: to },
    })
    return data
  })
}

// 4. Speed to lead — median / buckets over the rolling window.
export function loadSpeedToLead({ from, to }) {
  return section(async () => {
    const { data } = await api.get('/sales/speed-to-lead/summary/', {
      params: { days: inclusiveDays(from, to) },
    })
    return data
  })
}

// 5. Dialer agent performance — per-dispatcher calls / handle time.
export function loadAgentPerformance({ from, to }) {
  return section(async () => {
    const { data } = await api.get('/dialer/reports/calls/agent-performance/', {
      params: { start_date: from, end_date: to, tz_offset: -new Date().getTimezoneOffset() },
    })
    return data
  })
}

// 6. Technician reliability scorecard (range via days).
export function loadTechReliability({ from, to }) {
  return section(async () => {
    const { data } = await api.get('/territories/technicians/reliability/', {
      params: { days: inclusiveDays(from, to) },
    })
    return data
  })
}

// 6b. Per-tech ops metrics (AP-7-00463) — exact date_from/date_to; merge by employee_id.
export function loadTechOpsScorecard({ from, to }) {
  return section(async () => {
    const { data } = await api.get('/reports/analytics/tech-scorecard/', {
      params: { date_from: from, date_to: to },
    })
    return data
  })
}

export function loadTechOpsScorecardDrilldown({ employeeId, metric, from, to }) {
  return api.get('/reports/analytics/tech-scorecard/drilldown/', {
    params: {
      employee_id: employeeId,
      metric,
      date_from: from,
      date_to: to,
    },
  }).then(({ data }) => data)
}

// 6c. Weekly Cash Payments (AP-7-00463 AC-100). Own week picker (Mon-Sun), NOT the
// shared date range. Empty weekStart = the company's current week.
export function loadTechCashPayments({ weekStart }) {
  return section(async () => {
    const params = weekStart ? { week_start: weekStart } : {}
    const { data } = await api.get('/reports/analytics/tech-cash/', { params })
    return data
  })
}

export function loadTechCashDrilldown({ employeeId, weekStart }) {
  return api.get('/reports/analytics/tech-cash/drilldown/', {
    params: { employee_id: employeeId, week_start: weekStart },
  }).then(({ data }) => data)
}

// 7. Revenue by appliance type (FSJ-6-00014 Phase 2).
export function loadRevenueByAppliance({ from, to }) {
  return section(async () => {
    const { data } = await api.get('/reports/analytics/revenue-by-appliance/', {
      params: { date_from: from, date_to: to },
    })
    return data
  })
}

// 8. First-time-fix % (FSJ-6-00014 Phase 2).
export function loadFirstTimeFix({ from, to }) {
  return section(async () => {
    const { data } = await api.get('/reports/analytics/first-time-fix/', {
      params: { date_from: from, date_to: to },
    })
    return data
  })
}

// Load every section in parallel; the view decides how to render each envelope.
export async function loadOperationsReport({ from, to, compare }) {
  const [channel, cancellations, missed, speed, agents, tech, techOps, appliance, ftf] = await Promise.all([
    loadChannelAttribution({ from, to, compare }),
    loadCancellations({ from, to }),
    loadMissedCalls({ from, to }),
    loadSpeedToLead({ from, to }),
    loadAgentPerformance({ from, to }),
    loadTechReliability({ from, to }),
    loadTechOpsScorecard({ from, to }),
    loadRevenueByAppliance({ from, to }),
    loadFirstTimeFix({ from, to }),
  ])
  return { channel, cancellations, missed, speed, agents, tech, techOps, appliance, ftf }
}

export default { loadOperationsReport }
